import {Component, HostListener, OnInit, ChangeDetectionStrategy} from "@angular/core";
import {NgForm} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {NzMessageService} from "ng-zorro-antd/message";
import {lastValueFrom} from "rxjs";
import {SchemaKeywordDto, KeywordType, PerFormaAPIService} from "src/app/services/pfapi.service";
import {KeywordTypes, KeywordTypeLabels} from "src/app/models/performa-enums";

@Component({
    selector: "app-keyword-edit",
    templateUrl: "./keyword-edit.component.html",
    styleUrl: "./keyword-edit.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class KeywordEditComponent implements OnInit {
    constructor(
        private readonly api: PerFormaAPIService,
        private route: ActivatedRoute,
        private router: Router,
        private message: NzMessageService
    ) {}

    loading = true;
    isEditMode = false;
    isBeingUsed = false;
    regexValid = true;
    today = new Date();

    keyword: SchemaKeywordDto = {
        Id: null,
        Name: "",
        KeywordType: KeywordTypes.Alphanumeric,
        Regex: "",
        DateFormat: ""
    };

    // the following object is used to check
    // if any unsaved changes are present when
    // the user attempts to navigate away
    originalKeyword: SchemaKeywordDto | null = null;

    async ngOnInit() {
        this.route.queryParams.subscribe(p => {
            if (p.created === "true") {
                this.message.success("Created keyword");
            }
        });

        await this.checkEditMode();
    }

    checkEditMode = async () => {
        const id = this.route.snapshot.queryParamMap.get("id");

        if (!id) {
            this.loading = false;
            return;
        }

        this.isEditMode = true;

        try {
            this.keyword = await lastValueFrom(this.api.getApiConfigureKeywordsId(Number(id)));
            this.originalKeyword = JSON.parse(JSON.stringify(this.keyword));
            this.checkIfKeywordBeingUsed();
        } catch (e) {
            const errors = e.error?.ModelState?.status;

            if (errors && Array.isArray(errors)) {
                this.message.error(errors[0]);
            }
        } finally {
            this.loading = false;
        }
    };

    save = async (frm: NgForm) => {
        frm.control.markAllAsTouched();

        if (!frm.valid || !this.regexValid) return;

        this.loading = true;

        try {
            if (this.isEditMode) {
                await lastValueFrom(this.api.putApiConfigureKeywords(this.keyword));
                this.message.success("Modified keyword");
            } else {
                const resp = await lastValueFrom(this.api.postApiConfigureKeywords(this.keyword));
                this.keyword = resp;
                this.isEditMode = true;
                this.checkIfKeywordBeingUsed();
                this.router.navigateByUrl(`/configure/keywordedit?id=${resp.Id}&created=true`);
            }

            this.updateOriginalData();
            frm.control.markAsUntouched();
        } catch (e) {
            const errors = e.error?.ModelState?.status;

            if (errors && Array.isArray(errors)) {
                this.message.error(errors[0]);
            }
        } finally {
            this.loading = false;
        }
    };

    remove = async () => {
        if (!confirm("Are you sure you want to delete this keyword?")) {
            return;
        }
        try {
            this.loading = true;
            await lastValueFrom(this.api.deleteApiConfigureKeywordsId(Number(this.keyword.Id)));
            this.router.navigateByUrl("/configure/keywords");
        } catch (e) {
            const errors = e.error?.ModelState?.status;

            if (errors && Array.isArray(errors)) {
                this.message.error(errors[0]);
            }
        } finally {
            this.loading = false;
        }
    };

    // when the regex validation string changes, check the validity
    onRegexChange(regexValue: string) {
        if (regexValue.length == 0) {
            this.regexValid = true;
            return;
        }

        try {
            new RegExp(regexValue);
            this.regexValid = true;
        } catch {
            this.regexValid = false;
        }
    }

    // if the keyword type changes to anything other than Date, remove the date format string
    onKeywordTypeChange(type: KeywordType) {
        if (type == KeywordTypes.Alphanumeric || type == KeywordTypes.Numeric) {
            this.keyword.DateFormat = "";
        }
    }

    //#region Helper Methods

    // checks if a keyword is being used in the system
    checkIfKeywordBeingUsed = async () => {
        if (!this.keyword.Id) return false;

        try {
            this.isBeingUsed = await lastValueFrom(this.api.getApiConfigureKeywordsIdInUse(Number(this.keyword.Id)));
        } catch (e) {
            const errors = e.error?.ModelState?.status;

            if (errors && Array.isArray(errors)) {
                console.log(errors);
            }
        }
    };

    // convert a keyword type number to a name
    getKeywordTypeName(type: number): string {
        return KeywordTypeLabels[type] || "";
    }

    // check for unsaved changes
    isUnsavedChanges(): boolean {
        if (!this.originalKeyword) {
            return false;
        }

        return JSON.stringify(this.keyword) !== JSON.stringify(this.originalKeyword);
    }

    // listen for browser refresh/close
    @HostListener("window:beforeunload", ["$event"])
    beforeUnload(event: BeforeUnloadEvent) {
        if (this.isUnsavedChanges()) {
            event.preventDefault();

            // Legacy/fallback: Set if the property exists (future-proofing)
            if ("returnValue" in event) {
                event.returnValue = true;
            }
        }
    }

    // when various sections are saved, update the original data to mark it "clean"
    updateOriginalData = () => {
        const newKeyword: SchemaKeywordDto = JSON.parse(JSON.stringify(this.keyword));

        this.originalKeyword.Name = newKeyword.Name;
        this.originalKeyword.KeywordType = newKeyword.KeywordType;
        this.originalKeyword.Regex = newKeyword.Regex;
        this.originalKeyword.DateFormat = newKeyword.DateFormat;
    };

    //#endregion
}
